import React, { useState, useEffect, Component, ErrorInfo, ReactNode } from 'react';
import {
  Card,
  Button,
  Table,
  Space,
  Tag,
  Input,
  Modal,
  Row,
  Col,
  Typography,
  Avatar,
  Statistic,
  Tabs,
  Spin,
  message,
  Upload,
  Progress,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { UploadProps } from 'antd/es/upload';
import {
  PlusOutlined,
  UserOutlined,
  PhoneOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  DownloadOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '@/services/api.service';
import { postExcelImportWithRetry } from '@/utils/excelImportUpload';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class CustomerPageErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[CustomersPage Error Boundary] Error caught:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '40px' }}>
          <Typography.Title level={4}>Error loading Customers</Typography.Title>
          <pre style={{ background: '#f5f5f5', padding: 10 }}>{this.state.error?.toString()}</pre>
        </div>
      );
    }
    return this.props.children;
  }
}

const { Title, Text } = Typography;
const { Search } = Input;

const CUSTOMERS_QUERY_KEY = ['sales-customers'];
const CUSTOMERS_STATS_KEY = ['sales-customers-statistics'];

function unwrap<T>(res: unknown): T {
  const data = (res as any)?.data ?? res;
  return (data?.data ?? data) as T;
}

interface CustomerApi {
  id: string;
  customer_code: string;
  customer_name: string;
  customer_type: string;
  contact_person?: string;
  email?: string;
  phone?: string;
  billing_city?: string;
  billing_country?: string;
  billing_address_line1?: string;
  tax_registration_number?: string;
  credit_limit?: number;
  payment_terms?: string;
  is_active?: boolean;
  created_at?: string;
}

function displayCustomerType(t: string): string {
  if (t === 'Company') return 'Corporate';
  return t || '—';
}

const CustomersPageContent: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const customersListPath = location.pathname.startsWith('/platform')
    ? '/platform/foundation/customers'
    : '/growth/customers';
  const [searchText, setSearchText] = useState('');
  const debouncedSearch = useDebouncedValue(searchText.trim(), 300);
  const [isExcelBusy, setIsExcelBusy] = useState(false);
  const [uploadPct, setUploadPct] = useState<number | null>(null);
  const [customerPage, setCustomerPage] = useState(1);
  const [customerPageSize, setCustomerPageSize] = useState(20);

  const {
    data: customersRaw = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: [...CUSTOMERS_QUERY_KEY, debouncedSearch || ''],
    queryFn: async () => {
      const res = await api.get('/sales/customers', {
        params: debouncedSearch ? { search: debouncedSearch } : undefined,
      });
      const list = unwrap<CustomerApi[]>(res);
      return Array.isArray(list) ? list : [];
    },
  });

  const { data: statistics } = useQuery({
    queryKey: CUSTOMERS_STATS_KEY,
    queryFn: async () => {
      const res = await api.get('/sales/customers/statistics');
      return unwrap<{
        totalCustomers: number;
        activeCustomers: number;
        inactiveCustomers: number;
      }>(res);
    },
  });

  useEffect(() => {
    setCustomerPage(1);
  }, [debouncedSearch, customersRaw]);

  const columns: ColumnsType<CustomerApi> = [
    {
      title: 'Code',
      dataIndex: 'customer_code',
      key: 'customer_code',
      width: 120,
      sorter: (a, b) => (a.customer_code || '').localeCompare(b.customer_code || ''),
    },
    {
      title: 'Customer',
      key: 'details',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.customer_name}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            <UserOutlined /> {record.contact_person || '—'}
          </Text>
          <Text type="secondary" style={{ fontSize: '11px' }}>
            <PhoneOutlined /> {record.phone || '—'}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Location',
      key: 'location',
      width: 120,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text>{record.billing_city || '—'}</Text>
          <Text type="secondary" style={{ fontSize: '11px' }}>
            {record.billing_country || '—'}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'customer_type',
      key: 'customer_type',
      width: 100,
      render: (t: string) => {
        const label = displayCustomerType(t);
        const colors: { [key: string]: string } = {
          Corporate: 'blue',
          Individual: 'green',
          Government: 'purple',
        };
        return <Tag color={colors[label] || 'default'}>{label}</Tag>;
      },
    },
    {
      title: 'Credit limit',
      dataIndex: 'credit_limit',
      key: 'credit_limit',
      width: 120,
      align: 'right',
      render: (v: number) => <Text>{Number(v ?? 0).toLocaleString()}</Text>,
    },
    {
      title: 'Status',
      key: 'status',
      width: 100,
      render: (_, record) =>
        record.is_active ? <Tag color="success">Active</Tag> : <Tag>Inactive</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 220,
      render: (_, record) => (
        <Space>
          <Button type="text" size="small" icon={<EyeOutlined />} onClick={() => handleView(record)}>
            Customer 360
          </Button>
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={() => navigate(`${customersListPath}/${record.id}/edit`)}
          />
          <Button
            type="text"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          />
        </Space>
      ),
    },
  ];

  const handleView = (customer: CustomerApi) => {
    navigate(`/growth/customers/${customer.id}/360`);
  };

  const handleDelete = (customer: CustomerApi) => {
    Modal.confirm({
      title: 'Delete customer',
      content: `Delete "${customer.customer_name}"?`,
      okText: 'Delete',
      okType: 'danger',
      async onOk() {
        await api.delete(`/sales/customers/${customer.id}`);
        message.success('Customer deleted');
        await queryClient.invalidateQueries({ queryKey: CUSTOMERS_QUERY_KEY });
        await queryClient.invalidateQueries({ queryKey: CUSTOMERS_STATS_KEY });
        await queryClient.invalidateQueries({ queryKey: ['customers'] });
      },
    });
  };

  const triggerBlobDownload = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDownloadTemplate = async () => {
    setIsExcelBusy(true);
    try {
      const res = await api.getClient().get('/sales/customers/excel/template', {
        responseType: 'blob',
      });
      triggerBlobDownload(
        new Blob([res.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
        'customers-import-template.xlsx'
      );
      message.success('Template downloaded');
    } catch (err: any) {
      message.error(err?.response?.data?.message || err?.message || 'Download failed');
    } finally {
      setIsExcelBusy(false);
    }
  };

  const handleExportExcel = async () => {
    setIsExcelBusy(true);
    try {
      const res = await api.getClient().get('/sales/customers/excel/export', {
        responseType: 'blob',
      });
      const dateStr = new Date().toISOString().slice(0, 10);
      triggerBlobDownload(
        new Blob([res.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
        `customers-${dateStr}.xlsx`
      );
      message.success('Export downloaded');
    } catch (err: any) {
      message.error(err?.response?.data?.message || err?.message || 'Export failed');
    } finally {
      setIsExcelBusy(false);
    }
  };

  const handleImportExcel: UploadProps['customRequest'] = async (options) => {
    const { file, onError, onSuccess } = options;
    const formData = new FormData();
    formData.append('file', file as File);
    setIsExcelBusy(true);
    setUploadPct(0);
    try {
      const res = await postExcelImportWithRetry(api.getClient(), '/sales/customers/excel/import', formData, {
        onUploadProgress: (p) => setUploadPct(p),
      });
      const body = res.data as {
        success?: boolean;
        message?: string;
        data?: { created: number; updated: number; errors?: string[] };
      };
      if (body?.success !== false) {
        const d = body?.data;
        const errList = d?.errors?.length ? ` (${d.errors.length} row message(s))` : '';
        message.success((body?.message || 'Import completed') + errList);
        if (d?.errors?.length) {
          Modal.warning({
            title: 'Import notes',
            width: 560,
            content: (
              <div style={{ maxHeight: 280, overflow: 'auto' }}>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {d.errors!.slice(0, 50).map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
                {d.errors!.length > 50 ? <Text type="secondary">…and more</Text> : null}
              </div>
            ),
          });
        }
        await queryClient.invalidateQueries({ queryKey: CUSTOMERS_QUERY_KEY });
        await queryClient.invalidateQueries({ queryKey: CUSTOMERS_STATS_KEY });
        await queryClient.invalidateQueries({ queryKey: ['customers'] });
        onSuccess?.(body, file as any);
      } else {
        throw new Error(body?.message || 'Import failed');
      }
    } catch (err: any) {
      message.error(err?.response?.data?.message || err?.message || 'Import failed');
      onError?.(err as Error);
    } finally {
      setIsExcelBusy(false);
      setUploadPct(null);
    }
  };

  const totalCustomers = statistics?.totalCustomers ?? customersRaw.length;
  const activeCustomers = statistics?.activeCustomers ?? customersRaw.filter((c) => c.is_active).length;
  const inactiveCustomers = statistics?.inactiveCustomers ?? Math.max(0, totalCustomers - activeCustomers);

  if (isError) {
    return (
      <div>
        <Title level={2}>Customers</Title>
        <Card>
          <Text type="danger">Failed to load customers. {(error as Error)?.message}</Text>
          <Button type="primary" onClick={() => refetch()} style={{ marginTop: 8 }}>
            Retry
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>Customers</Title>
        <Text type="secondary">Manage customer master data, credit, and Excel import/export</Text>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total customers"
              value={totalCustomers}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#145A54' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic title="Active" value={activeCustomers} valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic title="Inactive" value={inactiveCustomers} valueStyle={{ color: '#999' }} />
          </Card>
        </Col>
      </Row>

      <Card>
        {uploadPct !== null && (
          <div style={{ marginBottom: 12 }}>
            <Text type="secondary">Uploading import file…</Text>
            <Progress percent={uploadPct} size="small" />
          </div>
        )}
        <div
          style={{
            marginBottom: '16px',
            display: 'flex',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '8px',
          }}
        >
          <Space wrap>
            <Search
              placeholder="Search code, name, email…"
              allowClear
              style={{ width: 300 }}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            <Button icon={<DownloadOutlined />} onClick={handleDownloadTemplate} disabled={isExcelBusy}>
              Template
            </Button>
            <Button icon={<DownloadOutlined />} onClick={handleExportExcel} disabled={isExcelBusy}>
              Export
            </Button>
            <Upload
              accept=".xlsx,.xls"
              showUploadList={false}
              customRequest={handleImportExcel}
              disabled={isExcelBusy}
            >
              <Button icon={<UploadOutlined />} loading={isExcelBusy}>
                Import
              </Button>
            </Upload>
          </Space>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate(`${customersListPath}/new`)}
          >
            Add customer
          </Button>
        </div>

        <Spin spinning={isLoading}>
          <Table
            columns={columns}
            dataSource={customersRaw}
            rowKey="id"
            pagination={{
              current: customerPage,
              pageSize: customerPageSize,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              showTotal: (total) => `Total ${total} customers`,
              onChange: (page, size) => {
                setCustomerPage(page);
                setCustomerPageSize(size);
              },
            }}
            size="small"
          />
        </Spin>
      </Card>
    </div>
  );
};

export const CustomersPage: React.FC = () => {
  return (
    <CustomerPageErrorBoundary>
      <CustomersPageContent />
    </CustomerPageErrorBoundary>
  );
};
